import { useEffect } from "react"
import { ZonePolicyDueDateType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { editZonePolicyDue } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import { DialogClose } from "../ui/dialog"
import { Input } from "../ui/input"

const formSchema = z.object({
  before_due_reminder: z.coerce
    .number()
    .min(1, "Before Due Reminder is required"),
  overdue_reminder: z.coerce.number().min(1, "Over Due Reminder is required"),
  disconnection_reminder: z.coerce
    .number()
    .min(1, "Disconnection Reminder is required"),
})

export default function EditZonesPolicyForm({
  zonePolicyData,
  setShowEditModal,
}: {
  zonePolicyData?: ZonePolicyDueDateType
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()

  const queryClient = useQueryClient()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      before_due_reminder: 0,
      overdue_reminder: 0,
      disconnection_reminder: 0,
    },
  })

  useEffect(() => {
    if (zonePolicyData) {
      form.reset({
        before_due_reminder: zonePolicyData?.before_due_reminder,
        overdue_reminder: zonePolicyData?.overdue_reminder,
        disconnection_reminder: zonePolicyData?.disconnection_reminder,
      })
    }
  }, [zonePolicyData, form.reset])

  const mutation = useMutation({
    mutationFn: async (data: ZonePolicyDueDateType) => {
      if (zonePolicyData?.id) {
        await editZonePolicyDue(zonePolicyData?.id, data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policyDetails"] })
      toast({
        description: "Zones Policy Due updated successfully.",
      })
      setShowEditModal(false)
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as ZonePolicyDueDateType)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/zone-settings/zone-policy-due-date",
            label: "Policy Due Date",
          },
        ]}
      >
        Edit Policy Due Date
      </Header>
      <DropdownMenuSeparator className="my-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="before_due_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Before Due Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="overdue_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Overdue Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="disconnection_reminder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Disconnection Reminder</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </Card>

          <div className="flex flex-row justify-end gap-2">
            <DialogClose>
              <Button type="button" variant="outline" className="w-28">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="w-28"
              disabled={mutation.isPending}
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
